/**
 * The auto-approve clock, and the one case where there isn't one.
 *
 * docs/01 §4.1–4.2: most proposals approve themselves four hours after they are
 * raised unless somebody objects, and **money never does**. A purchase sits
 * pending until the other two owners physically tap, however long that takes.
 *
 * Those are two genuinely different states, so they get two genuinely different
 * treatments — a running countdown against a lock. Rendering a purchase with a
 * greyed-out timer would suggest the timer merely stopped, which is exactly the
 * misreading that gets money approved by accident.
 *
 * `now` is always passed in. This component has no timer of its own; the board
 * owns the tick so every card on the screen agrees about the time.
 */

import type { Proposal, ProposalType } from '@office/domain'
import { DEFAULT_REMINDER_LEAD_MINUTES } from '@office/domain/proposals'
import '../chores/phone.css'
import './proposals.css'

const MINUTE = 60_000
const HOUR = 3_600_000
const DAY = 86_400_000

/** The window in which the reminder push goes out (docs/01 §4.2). */
export const REMINDER_LEAD_MS = DEFAULT_REMINDER_LEAD_MINUTES * MINUTE

/** "3h 12m", "12m", "under a minute". Never a bare number of milliseconds. */
export function formatRemaining(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return 'any moment'
  if (ms < MINUTE) return 'under a minute'
  if (ms < HOUR) return `${Math.floor(ms / MINUTE)}m`
  if (ms < DAY) {
    const h = Math.floor(ms / HOUR)
    const m = Math.floor((ms % HOUR) / MINUTE)
    return m === 0 ? `${h}h` : `${h}h ${m}m`
  }
  const d = Math.floor(ms / DAY)
  const h = Math.floor((ms % DAY) / HOUR)
  return h === 0 ? `${d}d` : `${d}d ${h}h`
}

export interface AutoApproveProps {
  proposal: Proposal
  type: ProposalType | null
  /** Corrected office time from the board's single tick. */
  now: number
  /** How many eligible voters have not tapped yet. */
  awaitingCount: number
}

export function AutoApprove({ proposal, type, now, awaitingCount }: AutoApproveProps) {
  // The registry is authoritative: a purchase row carrying a stray deadline
  // from a bad migration is still a purchase.
  if (type?.isMoneyRelated === true) {
    return (
      <p className="ph-chip pr-clock" data-testid="money-lock" data-tone="accent">
        <span aria-hidden="true">🔒 </span>
        Money never auto-approves — this waits until everyone taps
      </p>
    )
  }

  const at = proposal.autoApproveAt
  if (at === null) {
    return (
      <p className="ph-hint" data-testid="no-deadline">
        No deadline — this waits until everyone votes
      </p>
    )
  }

  const remaining = at - now
  const soon = remaining <= REMINDER_LEAD_MS

  return (
    <p
      className="ph-chip pr-clock"
      data-testid="auto-approve"
      data-tone={soon ? 'warn' : 'neutral'}
    >
      <span aria-hidden="true">⏳ </span>
      {remaining <= 0
        ? 'Approving now unless someone says no'
        : `Auto-approves in ${formatRemaining(remaining)} unless someone says no`}
      {soon && awaitingCount > 0 ? ` · ${awaitingCount} still to vote` : ''}
    </p>
  )
}
